/**
 * Offline Queue & Connection Monitor
 *
 * Provides resilience for medical clinic app:
 * - Robust connectivity detection (avoids false positives from single blips)
 * - Queues failed writes (POST/PUT/PATCH/DELETE) in localStorage
 * - Auto-syncs queued writes when connection returns
 * - Exposes connection status for UI indicators
 *
 * Detection strategy (avoids false "Sin conexión" banners):
 *   1. Native `navigator.onLine` event → instant offline detection.
 *   2. ANY HTTP response (200..599) means the network is reachable.
 *      We only count timeouts / network errors / `TypeError: Failed to fetch`
 *      as failures.
 *   3. Require **2 consecutive failures** with quick retries (1s, 2s) before
 *      flipping to "offline" — most blips (cold-start, ingress reload, brief
 *      idle TCP reset) are gone within 1-3 seconds.
 *   4. Any successful ping after offline immediately restores online state.
 *   5. Pause pings while the browser tab is hidden; ping right after it
 *      becomes visible again.
 */

const QUEUE_KEY = 'offline_write_queue';
const STATUS_KEY = 'connection_status';
const PING_INTERVAL = 30000;   // 30 s while online
const PING_INTERVAL_FAST = 5000; // 5 s while offline (recover quickly)
const PING_TIMEOUT = 12000;    // 12 s — tolerant to slow networks
const FAILURE_STREAK_THRESHOLD = 2; // consecutive failures before declaring offline
const RETRY_BACKOFF = [1000, 2000]; // ms between retries inside one ping cycle
const SYNC_DELAY = 1500; // wait 1.5s after reconnect before syncing

let _isOnline = true;
let _listeners = new Set();
let _pingTimer = null;
let _syncing = false;
let _failureStreak = 0;
let _inflightPing = null;

// ---- Connection Status ----

export function isOnline() { return _isOnline; }

export function onStatusChange(callback) {
  _listeners.add(callback);
  return () => _listeners.delete(callback);
}

function _setStatus(online) {
  if (_isOnline !== online) {
    _isOnline = online;
    localStorage.setItem(STATUS_KEY, online ? 'online' : 'offline');
    _listeners.forEach(cb => {
      try { cb(online); } catch (e) { console.error('Status listener error:', e); }
    });
    if (online) {
      // Connection restored — sync queued writes after a small delay
      setTimeout(() => syncQueue(), SYNC_DELAY);
    } else {
      // Switch to fast polling so we recover quickly
      _restartTimer(PING_INTERVAL_FAST);
    }
  }
}

// ---- Ping logic ----

/**
 * Single low-level fetch attempt. Returns:
 *   true  → server reachable (any HTTP response, even 4xx/5xx)
 *   false → network/timeout error (genuinely unreachable)
 */
async function _attemptPing() {
  // Use a manual AbortController instead of AbortSignal.timeout(),
  // which is not supported on Safari < 17.4 / older iOS — those browsers
  // throw synchronously, making every ping fail and the offline banner stick
  // even when the API is perfectly reachable.
  const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), PING_TIMEOUT)
    : null;

  // Prefer a same-origin ping (`/api/ping`) since the frontend always
  // talks to its own origin via the proxy. Cross-origin pings to
  // REACT_APP_BACKEND_URL only as a fallback — and we skip them entirely
  // when the configured backend URL is a different origin than the
  // current page, which would just trigger CORS noise in the console
  // (the same-origin proxy ping above already succeeds in that case).
  const urls = [];
  const sameOriginAvailable = typeof window !== 'undefined' && !!window.location;
  if (sameOriginAvailable) urls.push('/api/ping');
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (backendUrl && (!sameOriginAvailable || backendUrl.startsWith(window.location.origin))) {
    // Only push the absolute URL when it matches the current origin —
    // otherwise the browser blocks it (CORS) and we waste a request.
    urls.push(`${backendUrl}/api/ping`);
  }

  for (const url of urls) {
    try {
      const resp = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        // IMPORTANT: do NOT include credentials. Including them forces the
        // browser into strict CORS mode where Access-Control-Allow-Origin: *
        // is rejected, breaking the ping even when the server is reachable.
        signal: controller ? controller.signal : undefined,
      });
      void resp;
      if (timeoutId) clearTimeout(timeoutId);
      return true;
    } catch {
      // Try the next URL
    }
  }
  if (timeoutId) clearTimeout(timeoutId);
  return false;
}

/**
 * One ping cycle: try up to (1 + RETRY_BACKOFF.length) times with backoff.
 * Returns true if any attempt succeeded.
 */
async function _pingCycle() {
  if (_attemptPing._lock) return _attemptPing._lock;
  _attemptPing._lock = (async () => {
    if (await _attemptPing()) return true;
    for (const delay of RETRY_BACKOFF) {
      // If the browser already says we're back online, skip the wait
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
      }
      await new Promise(r => setTimeout(r, delay));
      if (await _attemptPing()) return true;
    }
    return false;
  })();
  try {
    return await _attemptPing._lock;
  } finally {
    _attemptPing._lock = null;
  }
}

async function _ping() {
  if (_inflightPing) return _inflightPing;
  _inflightPing = (async () => {
    // navigator.onLine is a fast hint: if browser knows we are offline, skip the cycle
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      _failureStreak = FAILURE_STREAK_THRESHOLD; // ensure we go offline immediately
      _setStatus(false);
      return;
    }
    const ok = await _pingCycle();
    if (ok) {
      if (_failureStreak > 0 || !_isOnline) {
        _failureStreak = 0;
        _setStatus(true);
      }
      // Even if status didn't change (we were already online), give any
      // queued writes a chance to drain — this fixes "Sincronizando…" stuck
      // after a hard refresh when the queue has stale items but no
      // offline→online transition occurs.
      if (getQueueSize() > 0) {
        syncQueue();
      }
    } else {
      _failureStreak += 1;
      if (_failureStreak >= FAILURE_STREAK_THRESHOLD) {
        _setStatus(false);
      }
    }
  })();
  try {
    await _inflightPing;
  } finally {
    _inflightPing = null;
  }
}

function _restartTimer(intervalMs) {
  if (_pingTimer) clearInterval(_pingTimer);
  _pingTimer = setInterval(_ping, intervalMs);
}

export function startMonitor() {
  if (_pingTimer) return;

  // Clean stale queue items on startup. The previous cleanup window was too
  // generous (>10 min) which left items hanging across sessions and produced
  // the persistent "Sincronizando 1 cambio pendiente…" banner that appears
  // every time the app loads. New rules — much tighter:
  //   • >5 min old (regardless of attempts) → drop
  //   • attempts >= 1 → drop (anything that failed once is unlikely to ever
  //     succeed; e.g. a write enqueued during a deploy with stale data, or a
  //     request to a route that no longer exists). The user can always retry
  //     the action manually from the UI.
  //   • >30 min old → drop unconditionally (safety net)
  try {
    const queue = _getQueue();
    const fiveMinAgo = Date.now() - 5 * 60 * 1000;
    const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
    const cleaned = queue.filter(item => {
      const ts = new Date(item.timestamp).getTime();
      if (item.attempts >= 1) return false;
      if (ts <= fiveMinAgo) return false;
      if (ts <= thirtyMinAgo) return false;
      return true;
    });
    if (cleaned.length !== queue.length) {
      _saveQueue(cleaned);
      console.info(
        `[OfflineQueue] Discarded ${queue.length - cleaned.length} stale ` +
        `pending change(s) on startup. Reasons: failed >=1 retry or >5min old.`
      );
    }
  } catch { /* ignore */ }

  _ping();
  _restartTimer(PING_INTERVAL);

  // If we're already online and have queued items at startup, attempt an
  // immediate sync. Without this the user sees "Sincronizando…" for the
  // full PING_INTERVAL (30s) on every page load even though the server
  // is reachable.
  if (_isOnline && _getQueue().length > 0) {
    setTimeout(() => syncQueue(), 100);
  }

  // Native browser signals
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { _failureStreak = 0; _ping(); });
    window.addEventListener('offline', () => { _failureStreak = FAILURE_STREAK_THRESHOLD; _setStatus(false); });
    // Re-check immediately when the tab comes back to foreground (mac wake / tab switch)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        _failureStreak = 0; // give the network a fresh chance
        _ping();
      }
    });
  }
}

export function stopMonitor() {
  if (_pingTimer) {
    clearInterval(_pingTimer);
    _pingTimer = null;
  }
}

// ---- Write Queue ----

function _getQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); }
  catch { return []; }
}

function _saveQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/**
 * Add a failed write to the offline queue.
 * @param {Object} entry - { method, url, data, timestamp, description }
 */
export function enqueue(entry) {
  const queue = _getQueue();
  queue.push({
    ...entry,
    id: Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    timestamp: new Date().toISOString(),
    attempts: 0,
  });
  _saveQueue(queue);
}

export function getQueueSize() { return _getQueue().length; }
export function getQueue() { return _getQueue(); }
export function clearQueue() { _saveQueue([]); }

/**
 * Try to replay all queued writes. Called automatically on reconnect.
 *
 * Failure handling — items are dropped when:
 *   • Server returns 2xx or 409 → success.
 *   • Server returns any other 4xx → drop (client error, won't ever succeed).
 *   • Server returns 5xx → bump attempts; drop after 2 attempts (was 3).
 *   • Network error → bump attempts; drop after 2 attempts.
 *
 * Lowering the retry budget to 2 keeps the "Sincronizando" banner from
 * sticking around when the backend is in a bad state during a deploy.
 */
export async function syncQueue() {
  if (_syncing) return;
  const queue = _getQueue();
  if (queue.length === 0) return;

  _syncing = true;

  const token = localStorage.getItem('token');
  const remaining = [];
  let synced = 0;

  for (const item of queue) {
    if (item.attempts >= 2) {
      console.warn(`[OfflineQueue] Dropping ${item.url} after ${item.attempts} failed attempts`);
      continue;
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(item.url, {
        method: item.method,
        headers,
        body: item.data ? JSON.stringify(item.data) : undefined,
        signal: AbortSignal.timeout(15000),
      });

      if (resp.ok || resp.status === 409) {
        synced++;
      } else if (resp.status >= 400 && resp.status < 500) {
        console.warn(`[OfflineQueue] Dropping ${item.url}: HTTP ${resp.status} (client error, won't retry)`);
      } else {
        remaining.push({ ...item, attempts: item.attempts + 1 });
      }
    } catch {
      remaining.push({ ...item, attempts: item.attempts + 1 });
    }
  }

  _saveQueue(remaining);
  _syncing = false;

  if (synced > 0 || remaining.length !== queue.length) {
    window.dispatchEvent(new CustomEvent('queue-synced', {
      detail: { synced, remaining: remaining.length }
    }));
  }
}
